import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardContent, Chip, IconButton, Grid, CircularProgress, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import FIXSessionConfig from './FIXSessionConfig';
import SymbolManagement from './SymbolManagement';
import LPConfigurationModal from './LPConfigurationModal';
import AddLPModal from './AddLPModal';
import EditLPModal from './EditLPModal';
import { getAvailableLPs, deleteLiquidityProvider } from '../../api/lpApi';
import { useAuth } from '../../provider/authProvider';
import { toast } from 'react-toastify';
import './SettingsResponsive.css';

// Prevent scroll wheel from changing numeric input values
const preventScrollOnNumberInputs = () => {
    const handleWheel = (e) => {
        if (e.target.type === 'number' && e.target.closest('.MuiTextField-root')) {
            e.preventDefault();
        }
    };

    // Add event listener to prevent scroll on number inputs
    document.addEventListener('wheel', handleWheel, { passive: false });
    
    // Cleanup function
    return () => {
        document.removeEventListener('wheel', handleWheel);
    };
};

const LPManagement = ({ onBack, onClose }) => {
    const { token, hasPermission } = useAuth();
    const [showFIXConfig, setShowFIXConfig] = useState(false);
    const [showSymbolManagement, setShowSymbolManagement] = useState(false);
    const [showLPConfiguration, setShowLPConfiguration] = useState(false);
    const [showAddLP, setShowAddLP] = useState(false);
    const [showEditLP, setShowEditLP] = useState(false);
    const [selectedLP, setSelectedLP] = useState(null);
    const [liquidityProviders, setLiquidityProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Prevent scroll wheel from changing numeric input values
    useEffect(() => {
        const cleanup = preventScrollOnNumberInputs();
        return cleanup;
    }, []);

    // Fetch available LPs from API
    const fetchAvailableLPs = async () => {
        if (!token) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const data = await getAvailableLPs(token);
            console.log('API Response:', data); // Debug log
            // Handle the API response structure: data.data.lPs (lowercase 'l')
            const lpArray = data?.data?.lPs || [];
            console.log('Processed LP Array:', lpArray); // Debug log
            setLiquidityProviders(lpArray);
        } catch (error) {
            console.error('Error fetching available LPs:', error);
            setError(error.message || 'Failed to fetch liquidity providers');
            toast.error('Failed to load liquidity providers');
        } finally {
            setLoading(false);
        }
    };

    // Load LPs on component mount
    useEffect(() => {
        fetchAvailableLPs();
    }, [token]);

    // Helper function to get LP icon based on type
    const getLPIcon = (lpType) => {
        switch (lpType?.toLowerCase()) {
            case 'prime broker':
                return <AccountBalanceIcon sx={{ fontSize: 24, color: '#00cfff' }} />;
            case 'ecn broker':
                return <ShowChartIcon sx={{ fontSize: 24, color: '#1de9b6' }} />;
            case 'exchange':
                return <CurrencyExchangeIcon sx={{ fontSize: 24, color: '#ff6b6b' }} />;
            default:
                return <AccountBalanceIcon sx={{ fontSize: 24, color: '#bfc9db' }} />;
        }
    };

    // Helper function to get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'connected':
                return '#1de9b6';
            case 'connecting':
                return '#ffd600';
            case 'disconnected':
                return '#ff6b6b';
            default:
                return '#bfc9db';
        }
    };

    const handleEdit = (lp) => {
        setSelectedLP(lp);
        setShowFIXConfig(true);
    };

    const handleEditLP = (lp) => {
        setSelectedLP(lp);
        setShowEditLP(true);
    };

    const handleSymbols = (lp) => {
        setSelectedLP(lp);
        setShowSymbolManagement(true);
    };

    const handleLPConfiguration = (lp) => {
        setSelectedLP(lp);
        setShowLPConfiguration(true);
    };

    const handleDisconnect = (lp) => {
        // Handle disconnect logic
        console.log('Disconnecting:', lp.name);
    };

    const handleDelete = async (lp) => {
        if (window.confirm(`Are you sure you want to delete "${lp.name}"? This action cannot be undone.`)) {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    toast.error('Authentication token not found');
                    return;
                }

                await deleteLiquidityProvider(token, lp.id);
                toast.success(`Liquidity Provider "${lp.name}" deleted successfully!`);
                // Refresh the LP list
                fetchAvailableLPs();
            } catch (error) {
                console.error('Error deleting LP:', error);
                toast.error('Failed to delete liquidity provider');
            }
        }
    };

    const handleRefreshAll = () => {
        fetchAvailableLPs();
    };

    const handleAddLP = () => {
        setShowAddLP(true);
    };

    const handleAddLPSuccess = (newLP) => {
        // Handle successful LP addition
        console.log('New LP added:', newLP);
        // Refresh the LP list to show the newly added LP
        fetchAvailableLPs();
        setShowAddLP(false);
    };

    const handleEditLPSuccess = (updatedLP) => {
        // Handle successful LP update
        console.log('LP updated:', updatedLP);
        // Refresh the LP list to show the updated LP
        fetchAvailableLPs();
        setShowEditLP(false);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ 
                p: 3, 
                borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                background: 'rgba(24, 31, 44, 0.9)'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={onBack} sx={{ color: '#fff' }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h4" sx={{ color: '#1de9b6', fontWeight: 700 }}>
                            QuantFIX LP Management
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            startIcon={<RefreshIcon />}
                            variant="outlined"
                            sx={{
                                color: '#00cfff',
                                borderColor: '#00cfff',
                                borderRadius: 1,
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                            onClick={handleRefreshAll}
                        >
                            Refresh All
                        </Button>
                        <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            sx={{
                                background: '#1de9b6',
                                color: '#000',
                                borderRadius: 1,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                    background: '#19b89b'
                                }
                            }}
                            onClick={handleAddLP}
                        >
                            Add LP
                        </Button>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: '#fff',
                                '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: '#bfc9db'
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Typography variant="body2" sx={{ color: '#bfc9db', ml: 6 }}>
                    Settings → LPs → Management Console
                </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                <Typography variant="h5" sx={{ color: '#fff', mb: 3, fontWeight: 600 }}>
                    Available Liquidity Providers
                </Typography>
                
                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress sx={{ color: '#1de9b6' }} />
                        <Typography variant="body1" sx={{ color: '#fff', ml: 2 }}>
                            Loading liquidity providers...
                        </Typography>
                    </Box>
                )}

                {/* Error State */}
                {error && !loading && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            mb: 3,
                            background: 'rgba(211, 47, 47, 0.1)',
                            border: '1px solid #d32f2f',
                            color: '#fff',
                            '& .MuiAlert-icon': { color: '#ff6b6b' }
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* LP Cards */}
                {!loading && !error && (
                <Grid container spacing={3}>
                        {Array.isArray(liquidityProviders) && liquidityProviders.length > 0 ? (
                            liquidityProviders.map((lp) => (
                                <Grid item xs={12} md={4} key={lp.id || lp.name}>
                                    <Card sx={{
                                        background: 'rgba(24, 31, 44, 0.8)',
                                        border: '1px solid rgba(6, 182, 212, 0.2)',
                                        borderRadius: 2,
                                        height: '100%',
                                        position: 'relative'
                                    }}>
                                        {/* Delete Icon - Top Right Corner */}
                                        {hasPermission('Settings.LPManagement.DeleteLP') && (
                                        <IconButton
                                            onClick={() => handleDelete(lp)}
                                            sx={{
                                                position: 'absolute',
                                                top: 2,
                                                right: 1,
                                                color: '#ff6b6b',
                                                background: 'rgba(24, 31, 44, 0.95)',
                                                border: '2px solid rgba(255, 107, 107, 0.3)',
                                                borderRadius: '50%',
                                                width: 28,
                                                height: 28,
                                                zIndex: 2,
                                                '&:hover': {
                                                    background: 'rgba(255, 107, 107, 0.2)',
                                                    color: '#ff5252',
                                                    border: '2px solid rgba(255, 107, 107, 0.6)',
                                                    transform: 'scale(1.1)',
                                                    boxShadow: '0 2px 8px rgba(255, 107, 107, 0.4)'
                                                },
                                                transition: 'all 0.2s ease-in-out'
                                            }}
                                            size="small"
                                        >
                                            <DeleteIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                        )}
                                        
                                        <CardContent sx={{ p: 3 }}>
                                    {/* Header */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    {getLPIcon(lp.lpType)}
                                            <Box>
                                                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                                                    {lp.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                                            {lp.lpType || 'Unknown Type'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                                        label={lp.isActive ? 'ACTIVE' : 'INACTIVE'}
                                            sx={{
                                                            background: lp.isActive ? '#1de9b6' : '#ff6b6b',
                                                color: '#000',
                                                fontWeight: 700,
                                                fontSize: '0.75rem'
                                            }}
                                        />
                                                </Box>
                                    </Box>

                                    {/* Metrics */}
                                    <Box sx={{ mb: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>Priority:</Typography>
                                                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                                                        {lp.priority || 'N/A'}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>Region:</Typography>
                                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                                                        {lp.region || 'N/A'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>ECN:</Typography>
                                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                                                        {lp.isEcn ? 'Yes' : 'No'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>Credit Limit:</Typography>
                                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                                                        {lp.creditLimit ? `$${lp.creditLimit.toFixed(2)}` : 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                            {/* Notes */}
                                            {lp.notes && (
                                                <Box sx={{ mb: 3 }}>
                                                    <Typography variant="body2" sx={{ color: '#bfc9db', mb: 1 }}>
                                                        Notes:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ 
                                                        color: '#fff', 
                                                        fontStyle: 'italic',
                                                        background: 'rgba(6, 182, 212, 0.1)',
                                                        p: 1,
                                                        borderRadius: 1,
                                                        border: '1px solid rgba(6, 182, 212, 0.2)'
                                                    }}>
                                                        {lp.notes}
                                                    </Typography>
                                                </Box>
                                            )}

                                    {/* Actions */}
                                            <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {hasPermission('Settings.LPManagement.ConnectionSetting') && (
                                        <Button
                                            startIcon={<EditIcon />}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                            color: '#00cfff',
                                                            borderColor: '#00cfff',
                                                borderRadius: 1,
                                                textTransform: 'none',
                                                flex: 1
                                            }}
                                            onClick={() => handleEdit(lp)}
                                        >
                                                        Connection Settings
                                        </Button>
                                        )}
                                                    {hasPermission('Settings.LPManagement.EditLP') && (
                                                    <Button
                                                        startIcon={<EditIcon />}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            color: '#ffd600',
                                                            borderColor: '#ffd600',
                                                            borderRadius: 1,
                                                            textTransform: 'none',
                                                            flex: 1
                                                        }}
                                                        onClick={() => handleEditLP(lp)}
                                                    >
                                                        Edit LP
                                                    </Button>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                        {hasPermission('Settings.LPManagement.Symbols') && (
                                        <Button
                                            startIcon={<SyncIcon />}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                color: '#ffd600',
                                                borderColor: '#ffd600',
                                                borderRadius: 1,
                                                textTransform: 'none',
                                                flex: 1
                                            }}
                                            onClick={() => handleSymbols(lp)}
                                        >
                                            Symbols
                                        </Button>
                                        )}
                                        {hasPermission('Settings.LPManagement.LPConfiguration') && (
                                        <Button
                                            startIcon={<SettingsIcon />}
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                color: '#9c27b0',
                                                borderColor: '#9c27b0',
                                                borderRadius: 1,
                                                textTransform: 'none',
                                                flex: 1
                                            }}
                                            onClick={() => handleLPConfiguration(lp)}
                                        >
                                            LP Configuration
                                        </Button>
                                        )}
                                        <Button
                                            startIcon={<LinkOffIcon />}
                                            variant="contained"
                                            size="small"
                                            sx={{
                                                background: '#ff6b6b',
                                                color: '#fff',
                                                borderRadius: 1,
                                                textTransform: 'none',
                                                flex: 1,
                                                '&:hover': {
                                                    background: '#ff5252'
                                                }
                                            }}
                                            onClick={() => handleDisconnect(lp)}
                                        >
                                            Disconnect
                                        </Button>
                                                </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    minHeight: 200,
                                    textAlign: 'center'
                                }}>
                                    <Typography variant="h6" sx={{ color: '#bfc9db', mb: 2 }}>
                                        No Liquidity Providers Found
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#7b879d', mb: 3 }}>
                                        Click "Add LP" to add your first liquidity provider
                                    </Typography>
                                    <Button
                                        startIcon={<AddIcon />}
                                        variant="contained"
                                        onClick={handleAddLP}
                                        sx={{
                                            background: '#1de9b6',
                                            color: '#000',
                                            borderRadius: 1,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            '&:hover': {
                                                background: '#19b89b'
                                            }
                                        }}
                                    >
                                        Add First LP
                                    </Button>
                                </Box>
                            </Grid>
                        )}
                </Grid>
                )}
            </Box>

            {/* Status Bar */}
            <Box sx={{
                p: 2,
                borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                background: 'rgba(24, 31, 44, 0.9)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#1de9b6' }} />
                        <Typography variant="body2" sx={{ color: '#fff' }}>Connected</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '2px', background: '#ff6b6b' }} />
                        <Typography variant="body2" sx={{ color: '#fff' }}>Market Open</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#ffd600' }}>📊</Typography>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                            Total LPs: {Array.isArray(liquidityProviders) ? liquidityProviders.length : 0}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#ffd600' }}>💰</Typography>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                            Active LPs: {Array.isArray(liquidityProviders) ? liquidityProviders.filter(lp => lp.isActive).length : 0}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: '#bfc9db' }}>🕐</Typography>
                    <Typography variant="body2" sx={{ color: '#fff' }}>
                        {new Date().toLocaleTimeString()}
                    </Typography>
                </Box>
            </Box>

            {/* Modals */}
            {showFIXConfig && (
                <FIXSessionConfig
                    open={showFIXConfig}
                    onClose={() => setShowFIXConfig(false)}
                    lp={selectedLP}
                />
            )}
            
            {showSymbolManagement && (
                <SymbolManagement
                    open={showSymbolManagement}
                    onClose={() => setShowSymbolManagement(false)}
                    lp={selectedLP}
                />
            )}

            {showLPConfiguration && (
                <LPConfigurationModal
                    open={showLPConfiguration}
                    onClose={() => setShowLPConfiguration(false)}
                    lp={selectedLP}
                />
            )}

            {showAddLP && (
                <AddLPModal
                    open={showAddLP}
                    onClose={() => setShowAddLP(false)}
                    onSuccess={handleAddLPSuccess}
                />
            )}

            {showEditLP && (
                <EditLPModal
                    open={showEditLP}
                    onClose={() => setShowEditLP(false)}
                    onSuccess={handleEditLPSuccess}
                    lp={selectedLP}
                />
            )}
        </Box>
    );
};

export default LPManagement; 